import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import type {
  DataResponse,
  KategoriaZakazky,
  KategorieZakaziek,
  StavZakazky,
} from "@/api/schemas";

/**
 * Kategórie zákaziek s počtami pre ľavý panel prehľadu (KOLO 172).
 * Počty idú podľa prepínača archívu — v archíve ukazujú archivované —
 * a od KOLA 174 v rámci zvoleného stavu (archív stav nefiltruje).
 */
export function useKategorieZakaziek(
  archiv = false,
  stav: StavZakazky | null = null,
  /** viac stavov čiarkou — pohľad Výroba (KOLO 242) */
  stavy: string | null = null,
): UseQueryResult<DataResponse<KategorieZakaziek>> {
  const stavPocty = archiv ? null : (stav ?? stavy);
  return useQuery({
    queryKey: queryKeys.zakazky.kategorie(archiv, stavPocty),
    queryFn: async () => {
      const params: Record<string, string | boolean> = {};
      if (archiv) params.archiv = true;
      if (!archiv && stav) params.stav = stav;
      else if (!archiv && stavy) params.stavy = stavy;
      const { data } = await api.get<DataResponse<KategorieZakaziek>>(
        "/kategorie-zakaziek",
        { params: Object.keys(params).length ? params : undefined },
      );
      return data;
    },
  });
}

// Mutácie invalidujú `zakazky.lists()` — pod ním sú kategórie aj zoznam
// a počty dlaždíc, ktoré nesú `kategoria_id` zákazky

export function useCreateKategoria(): UseMutationResult<
  DataResponse<KategoriaZakazky>,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (nazov: string) => {
      const { data } = await api.post<DataResponse<KategoriaZakazky>>(
        "/kategorie-zakaziek",
        { nazov },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.zakazky.lists() });
    },
  });
}

export function usePremenujKategoriu(): UseMutationResult<
  DataResponse<KategoriaZakazky>,
  Error,
  { id: string; nazov: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, nazov }) => {
      const { data } = await api.patch<DataResponse<KategoriaZakazky>>(
        `/kategorie-zakaziek/${id}`,
        { nazov },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.zakazky.lists() });
    },
  });
}

export function useZmazKategoriu(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/kategorie-zakaziek/${id}`);
    },
    onSuccess: () => {
      // Zákazky zmazanej kategórie sú bez kategórie — zoznam aj ich detail
      void qc.invalidateQueries({ queryKey: queryKeys.zakazky.lists() });
      void qc.invalidateQueries({ queryKey: queryKeys.zakazky.details() });
    },
  });
}
